// src/components/WeightSelectionModal/index.js
// Beautiful weight selection popup for weight-based items (unit_type: 2)
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Scale, Minus, Plus, ShoppingCart } from 'lucide-react';
import { settingsService } from '../../config/settingsService';
import './index.css';

// Default weight options when API doesn't provide them
const DEFAULT_WEIGHT_OPTIONS = [
  { label: '250g', value: 0.25 },
  { label: '500g', value: 0.5 },
  { label: '1 kg', value: 1 },
];

const WeightSelectionModal = ({ isOpen, onClose, product, onConfirm }) => {
  const [selectedWeight, setSelectedWeight] = useState(null);
  const [customWeight, setCustomWeight] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [closing, setClosing] = useState(false);
  const [customError, setCustomError] = useState('');

  // Build weight options from product or defaults
  const weightOptions = (() => {
    const config = product?.weight_config;
    if (config?.weight_options && config.weight_options.length > 0) {
      return config.weight_options.map(opt => ({
        label: opt.label || `${opt.value} ${product?.unit || 'kg'}`,
        value: parseFloat(opt.value)
      }));
    }
    return DEFAULT_WEIGHT_OPTIONS;
  })();

  const unit = product?.unit || 'kg';
  const pricePerUnit = parseFloat(product?.price || product?.flatPrice || 0);
  const minWeight = product?.weight_config?.scale_config?.min_weight || 0.1;
  const maxWeight = product?.weight_config?.scale_config?.max_weight || 10;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedWeight(null);
      setCustomWeight('');
      setIsCustom(false);
      setClosing(false);
      setCustomError('');
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 280);
  }, [onClose]);

  const handleWeightSelect = (weight) => {
    setSelectedWeight(weight);
    setIsCustom(false);
    setCustomWeight('');
    setCustomError('');
  };

  const handleCustomWeightChange = (value) => {
    setCustomError('');
    const numValue = value.replace(/[^0-9.]/g, '');
    // Allow only one decimal point
    const parts = numValue.split('.');
    const cleaned = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numValue;
    setCustomWeight(cleaned);
    setIsCustom(true);
    const parsed = parseFloat(cleaned);
    if (!isNaN(parsed) && parsed >= minWeight && parsed <= maxWeight) {
      setSelectedWeight(parsed);
    } else {
      setSelectedWeight(null);
    }
  };

  const adjustCustomWeight = (delta) => {
    if (!customWeight && delta > 0 && !selectedWeight) {
      setCustomError('User should choose the weight or enter the weight.');
      return;
    }
    setCustomError('');
    const current = parseFloat(customWeight) || 0;
    const step = 0.25;
    const newValue = Math.max(minWeight, Math.min(maxWeight, current + (delta * step)));
    const rounded = Math.round(newValue * 100) / 100;
    setCustomWeight(rounded.toString());
    setSelectedWeight(rounded);
    setIsCustom(true);
  };

  const currentWeight = selectedWeight || 0;
  const calculatedPrice = currentWeight * pricePerUnit;

  const handleConfirm = () => {
    if (!selectedWeight || selectedWeight <= 0) return;

    const weightedProduct = {
      ...product,
      // Store the selected weight
      selectedWeight: selectedWeight,
      weightUnit: unit,
      // Calculate the total price for this weight
      flatPrice: calculatedPrice,
      // Mark as weight-based for cart display
      isWeightBased: true,
      weightLabel: formatWeightLabel(selectedWeight, unit),
      // Quantity is always 1 for weight-based items (weight IS the quantity)
      quantity: 1,
      // Use both customization key and weight so different versions are distinct
      cartItemKey: `${product.cartItemKey || product.id}|w:${selectedWeight}`
    };

    onConfirm(weightedProduct);
    handleClose();
  };

  if (!isOpen || !product) return null;

  return createPortal(
    <div className={`weight-modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`weight-modal-container ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="weight-modal-header">
          <div className="weight-modal-title-row">
            <div className="weight-modal-icon">
              <Scale size={20} />
            </div>
            <div>
              <h3 className="weight-modal-title">Select Weight</h3>
              <p className="weight-modal-subtitle">{product.name}</p>
            </div>
          </div>
          <button className="weight-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {/* Product Info Strip */}
        <div className="weight-modal-product-strip">
          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              className="weight-modal-product-img"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="weight-modal-product-meta">
            <span className="weight-modal-price-label">Price per {unit}</span>
            <span className="weight-modal-price-value">{settingsService.formatPrice(pricePerUnit)}</span>
          </div>
        </div>

        {/* Quick Weight Options */}
        <div className="weight-modal-options-label">Choose a weight</div>
        <div className="weight-modal-options-grid">
          {weightOptions.map((option) => (
            <button
              key={option.value}
              className={`weight-option-chip ${selectedWeight === option.value && !isCustom ? 'selected' : ''
                }`}
              onClick={() => handleWeightSelect(option.value)}
            >
              <span className="weight-option-label">{option.label}</span>
              <span className="weight-option-price">
                {settingsService.formatPrice(option.value * pricePerUnit)}
              </span>
            </button>
          ))}
        </div>

        {/* Custom Weight Input */}
        {/* <div className="weight-modal-custom-section">
          <div className="weight-modal-custom-label">Or enter custom weight ({unit})</div>
          <div className="weight-modal-custom-input-row">
            <button 
              className="weight-custom-adjust-btn" 
              onClick={() => adjustCustomWeight(-1)}
              disabled={!isCustom && !customWeight}
            >
              <Minus size={16} />
            </button>
            <div className="weight-custom-input-wrapper">
              <input
                type="text"
                inputMode="decimal"
                className={`weight-custom-input ${isCustom ? 'active' : ''}`}
                placeholder={`Min ${minWeight} ${unit}`}
                value={customWeight}
                onChange={(e) => handleCustomWeightChange(e.target.value)}
                onFocus={() => {
                  setIsCustom(true);
                  if (!customWeight) {
                    setSelectedWeight(null);
                  }
                }}
              />
              <span className="weight-custom-unit">{unit}</span>
            </div>
            <button 
              className="weight-custom-adjust-btn" 
              onClick={() => adjustCustomWeight(1)}
            >
              <Plus size={16} />
            </button>
          </div>
          {customError && (
             <div className="weight-custom-error" style={{ color: '#dc2626', marginTop: '8px', fontSize: '13px' }}>{customError}</div>
          )}
          {isCustom && customWeight && parseFloat(customWeight) < minWeight && (
            <div className="weight-custom-error">Minimum weight is {minWeight} {unit}</div>
          )}
          {isCustom && customWeight && parseFloat(customWeight) > maxWeight && (
            <div className="weight-custom-error">Maximum weight is {maxWeight} {unit}</div>
          )}
        </div> */}

        {/* Total Price Preview */}
        <div className={`weight-modal-total ${selectedWeight || isCustom ? 'visible' : ''}`}>
          <div className="weight-total-breakdown">
            {/* <div className="weight-custom-calc">
              {formatWeightLabel(currentWeight, unit)} × {settingsService.formatPrice(pricePerUnit)}
            </div> */}
          </div>
          <div className="weight-total-amount">
            <span className="weight-total-label">Total</span>
            <span className="weight-total-value">{settingsService.formatPrice(calculatedPrice)}</span>
          </div>
        </div>

        {/* Confirm Button */}
        <button
          className={`weight-modal-confirm-btn ${selectedWeight ? 'enabled' : 'disabled'}`}
          onClick={handleConfirm}
          disabled={!selectedWeight || selectedWeight <= 0}
        >
          <ShoppingCart size={18} />
          <span>
            {selectedWeight
              ? `Add ${formatWeightLabel(selectedWeight, unit)} to Cart — ${settingsService.formatPrice(calculatedPrice)}`
              : 'Select a weight to continue'
            }
          </span>
        </button>
      </div>
    </div>,
    document.body
  );
};

// Helper to format weight nicely
function formatWeightLabel(weight, unit = 'kg') {
  if (weight === 0) return `0 ${unit}`;
  if (!weight || weight <= 0) return '';
  if (unit === 'kg') {
    if (weight < 1) {
      return `${Math.round(weight * 1000)}g`;
    }
    return weight % 1 === 0 ? `${weight} kg` : `${weight} kg`;
  }
  return `${weight} ${unit}`;
}

export default WeightSelectionModal;
